<script lang="ts">
	import { getContext, setContext, onDestroy, onMount, tick } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { writable } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import { Button } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import SelectRoomStep from 'src/components/Devices/DeviceWizard/AddEditSteps/SelectRoomStep.svelte';
	import { devices } from 'src/stores/lists/devices';
	import { performDeviceEditSave } from 'src/helpers/edit_device_save_helper';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	let editDevice: Device = device;
	let requestLoader = false;
	let selectedRef: HTMLElement = null;
	let updateFullHeight;

	$: if (device && editDevice?.id !== device?.id) {
		editDevice = device;
	}

	const createNewRoomStore = writable({
		newRoom: undefined,
		newRoomEditing: false,
	});

	setContext('newRoomStore', createNewRoomStore);

	const onDetailsSaved =
		getContext<(payload: { roomChanged: boolean; roomId?: number; shared?: boolean }) => void>(
			'onDetailsSaved',
		);

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const onSave = async () => {
		if (!editDevice?.name || editDevice.name.trim() === '') return;
		const initialRoomId = device.room_id;
		requestLoader = true;
		registerStickySave();
		try {
			await performDeviceEditSave(editDevice);
			devices.update(editDevice);
			onDetailsSaved?.({
				roomChanged: editDevice.room_id !== initialRoomId,
				roomId: editDevice.room_id,
				shared: editDevice.shared,
			});
		} finally {
			requestLoader = false;
			registerStickySave();
		}
	};

	$: isSaveDisabled = requestLoader || !editDevice?.name || editDevice.name.trim() === '';
	$: isAddingRoom = !!$createNewRoomStore?.newRoomEditing;

	const registerStickySave = () => {
		if (!stickyBottomAction) return;
		// Hide device-room sticky Save while the inline "Add room" form is open
		if ($showNewLayout && !isAddingRoom) {
			stickyBottomAction.set({
				onClick: onSave,
				loading: requestLoader,
				disabled: isSaveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			stickyBottomAction.set(null);
		}
	};

	// Re-register whenever save state, add-room mode, or layout mode changes
	$: if (stickyBottomAction) {
		$showNewLayout;
		isSaveDisabled;
		requestLoader;
		isAddingRoom;
		registerStickySave();
	}

	onMount(async () => {
		registerStickySave();
		// Previous tab's onDestroy may clear sticky after we mount — re-claim after tick
		await tick();
		registerStickySave();
	});

	onDestroy(() => {
		// Only clear if we still own the sticky action (avoid wiping the next tab's Save)
		stickyBottomAction?.update((current) =>
			current?.onClick === onSave ? null : (current ?? null),
		);
	});
</script>

<SelectRoomStep bind:editDevice bind:selectedRef bind:updateFullHeight {requestLoader} />

{#if !$showNewLayout}
	<div class="mt-l">
		<Button
			class="width-sm-full"
			variant="primary"
			size="m"
			responsiveSize={{ sm: 'm' }}
			loading={requestLoader}
			disabled={isSaveDisabled}
			icon="fas fa-save"
			on:click={onSave}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
{/if}
